import { useLoaderData } from '@remix-run/react';
import { htmlEscape } from 'escape-goat';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { ToastMessage } from '~/components/ui/ToastMessage';
import { patchProject } from '~/lib/.client/api/projects';
import { getAgentForCurrentChat, isCurrentChatUsingBoltAgent } from '~/lib/ai';
import { chatHooks } from '~/lib/chat-hooks';
import {
  BOLT_BOOTSTRAP_ID,
  BOLT_SUPABASE_CONNECT_MSG_PREFIX,
  DEFAULT_TITLE,
  GITHUB_SYNC_PREFIX,
} from '~/lib/constants';
import { type ChatHistory, createChat, updateChatHistory } from '~/lib/persistence/chats';
import { importStore } from '~/lib/projects';
import { importPromise } from '~/lib/projects/importStore';
import { getDescription, setDescription } from '~/lib/stores/description';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { workbenchStore } from '~/lib/stores/workbench';
import type { Message } from '~/types/chat';
import type { OrganizationData } from '~/types/organization';
import { debounceAndQueue } from '~/utils/debounce';
import { NOOP } from '~/utils/noops';
import { addWorkDir } from '~/utils/workDir';

const NO_PERSISTENCE = new Set([BOLT_BOOTSTRAP_ID]);

const NO_PERSISTENCE_PREFIXES = [GITHUB_SYNC_PREFIX, BOLT_SUPABASE_CONNECT_MSG_PREFIX];

const chatHistoryStore = new Map<string, Set<any>>();

const dateTimeFormatter = new Intl.DateTimeFormat(globalThis?.navigator?.language ?? 'en-US', {
  dateStyle: 'full',
  timeStyle: 'short',
});

interface ChatHistoryOptions {
  chatHistory?: Promise<ChatHistory>;
}

export function useChatHistory({ chatHistory }: ChatHistoryOptions = {}) {
  const { slug } = useLoaderData<{ slug: string }>();

  useEffect(() => {
    if (!chatHistory) {
      return;
    }

    async function loadChatHistory() {
      const history = await chatHistory;

      if (history && history.messages.length > 0) {
        const { messages, editedAt } = history;

        if (compareEditedAt(editedAt, projectStore.getEditedAt()) !== 0) {
          const editedAtDate = dateTimeFormatter.format(new Date(projectStore.getEditedAt()!));

          toast.info(
            <ToastMessage
              title="Chat history may be incomplete"
              message={'This project was last updated outside of Bolt on ' + editedAtDate + '.'}
            />,
            { autoClose: false },
          );
        }

        for (const message of messages) {
          if (message.type === 'supabase') {
            message.ignore = true;

            if (message.change.type === 'migration' && !Array.isArray(message.change.migrations)) {
              message.change.migrations = [];
            }
          }
        }

        // we fill the message filter so that we do not store messages again coming from the backend
        const messageFilter = chatHistoryStore.get(slug!) ?? new Set();

        for (const message of messages) {
          messageFilter.add(message.id);
        }

        chatHistoryStore.set(slug, messageFilter);

        projectStore.refreshContextSize({ messages, agent: getAgentForCurrentChat(), kind: 'load-history' });
      }

      importStore.setKey('chatLoadingStatus', 'LOADED');
    }

    loadChatHistory();
  }, [chatHistory]);

  useEffect(() => {
    const storeMessageHistory = debounceAndQueue(async (signal: AbortSignal, messages: readonly Message[]) => {
      const slug = projectStore.getSlug();

      if (messages.length === 0 || !slug) {
        return;
      }

      /**
       * Wait for the import to be ready.
       *
       * TODO: Why does this code care about this?
       */
      await importPromise.promise;

      signal.throwIfAborted();

      /**
       * Filter out message from the project import. Those get generated on every load
       * and should not be saved in IDB.
       */
      messages = messages.filter(({ id }) => {
        return shouldPersist(id);
      });

      if (messages.length === 0) {
        return;
      }

      /**
       * If the project needs to be forked we don't persist any messages until it
       * was forked we should happen as soon as the logged in user has sent a message.
       */
      if (projectStore.shouldFork()) {
        return;
      }

      const { firstArtifact } = workbenchStore;

      const projectId = projectStore.getProjectId();
      const activeOrganization = userStore.getActiveOrganization().get();

      if (!projectId) {
        // we cant create the chat if we don't have a project id
        return;
      }

      if (!getDescription()) {
        if (!firstArtifact?.title && !projectStore.title) {
          return;
        }

        const title = firstArtifact?.title || projectStore.title || DEFAULT_TITLE;

        // if we don't have a description yet, but we have a first artifact with a title, we update everything
        setDescription(title);

        // patch page title
        if (isCurrentChatUsingBoltAgent()) {
          // in claude code, this is set in apply annotations
          const indexHtml = workbenchStore.files.get()[addWorkDir('index.html')];
          const replacePattern = /<title data-default>(.*?)</;

          if (indexHtml?.type === 'file' && replacePattern.test(indexHtml.content)) {
            workbenchStore.writeFile('index.html', {
              type: 'file',
              content: indexHtml.content.replace(replacePattern, `<title>${htmlEscape(title)}<`),
            });
          }
        }

        // we don't have to wait for the title update
        patchProject(slug, { title }).catch(() => {
          // ignore any errors
        });
      }

      signal.throwIfAborted();

      await updateProject(activeOrganization);

      async function updateProject(activeOrganization: OrganizationData | null, retries = 0) {
        if (!projectId) {
          // we can't update the project if we don't have the project id
          return;
        }

        const messageFilter = chatHistoryStore.get(slug!) ?? new Set();

        const persist: any[] = [];

        for (const message of messages.values()) {
          const {
            id,
            role,
            content,
            createdAt,
            status,
            hidden,
            type,
            parts,
            annotations,
            experimental_attachments: experimentalAttachments,
          } = message;

          if (status !== 'complete') {
            continue;
          }

          /**
           * Filter out message from the project import. Those get generated on every load
           * and should not be saved in IDB.
           */
          if (messageFilter.has(id) || hidden) {
            continue;
          }

          /**
           * We do not persist Supabase changes that have not been applied.
           */
          if (message.type === 'supabase' && message.changeStatus === 'pending') {
            continue;
          }

          // we add the message to the filter so we don't add it again later
          messageFilter.add(id);

          persist.push({
            id,
            type,
            role,
            content,
            createdAt,
            annotations,
            parts,
            experimental_attachments: experimentalAttachments,
            ...(type === 'chat' && role === 'user' ? { input: message.input, renderHtml: message.renderHtml } : {}),
            ...(type === 'problems' ? { problems: message.problems, resolved: message.resolved } : {}),
            ...(type === 'supabase'
              ? { ignore: true, change: message.change, changeStatus: message.changeStatus }
              : {}),
            ...(role === 'user' && type === 'chat' ? { type, inspectedElement: message.inspectedElement } : {}),
          });
        }

        if (persist.length > 0) {
          chatHistoryStore.set(slug!, messageFilter);

          try {
            await updateChatHistory(projectId, persist);
          } catch {
            // if we fail to store the messages, we remove them from the list so they get retried the next time
            for (const message of messages) {
              messageFilter.delete(message.id);
            }

            if (retries < 2) {
              try {
                // persist the new chat first in case it does not exist yet
                await createChat({
                  projectId,
                  description: getDescription(),
                  activeOrganization,
                });
              } catch {
                // if we fail to create the chat, show an error and exit
                toast.error('Failed to persist the chat', { toastId: 'failed-to-persist-chat' });

                return;
              }

              // update the project
              await updateProject(activeOrganization, retries + 1);
            } else {
              toast.error('Failed to persist the chat messages');
            }
          }
        }
      }
    });

    chatHooks.messages.subscribe((messages, previousValue) => {
      // ignore initial call to subscribe
      if (!previousValue) {
        return;
      }

      storeMessageHistory(messages).catch(NOOP);
    });
  });
}

function compareEditedAt(a: Date | string | undefined, b: Date | string | undefined) {
  if (!a || !b) {
    // we cannot compare so we assume they are equal
    return 0;
  }

  return new Date(a).getTime() - new Date(b).getTime();
}

function shouldPersist(id: string) {
  return !NO_PERSISTENCE.has(id) && NO_PERSISTENCE_PREFIXES.every((prefix) => !id.startsWith(prefix));
}
